import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { getPhoneForState } from "@/data/contact";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, ChevronDown, MapPin } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { services } from "@/data/services";

const locations = [
  {
    state: "Washington",
    slug: "washington",
    services: [
      { name: "Photography", slug: "real-estate-photography" },
      { name: "Drone / Aerial", slug: "drone-photography" },
      { name: "Video Tours", slug: "real-estate-video" },
      { name: "Headshots", slug: "headshots" },
    ],
  },
  {
    state: "Arkansas",
    slug: "arkansas",
    services: [
      { name: "Photography", slug: "real-estate-photography" },
      { name: "Drone / Aerial", slug: "drone-photography" },
      { name: "Video Tours", slug: "real-estate-video" },
      { name: "Headshots", slug: "headshots" },
    ],
  },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isArkansas = location.pathname.startsWith("/arkansas");
  const phone = getPhoneForState(isArkansas ? "arkansas" : "washington");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Sticky Phone CTA - Mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-primary p-3 flex items-center justify-center gap-3">
        <Phone size={18} className="text-primary-foreground" />
        <a href={phone.href} className="text-primary-foreground font-semibold">
          Call Now: {phone.display}
        </a>
      </div>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-md border-b border-border"
            : "bg-transparent"
        }`}
      >
        {/* Top bar with phone - Desktop */}
        <div className="hidden md:block bg-primary/10 border-b border-border">
          <div className="container mx-auto px-6 py-2 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Premium Real Estate Media in Washington & Arkansas
            </p>
            <a
              href={phone.href}
              className="flex items-center gap-2 text-primary font-medium text-sm hover:text-primary/80 transition-colors"
            >
              <Phone size={14} />
              {phone.display}
            </a>
          </div>
        </div>

        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <span className="font-serif text-2xl text-foreground">
                <span className="font-semibold">luminate</span><span className="font-normal text-primary">media</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                  Services <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-card border-border z-50">
                  {services.map((service) => (
                    <DropdownMenuItem key={service.slug} asChild>
                      <Link
                        to={`/services/${service.slug}`}
                        className="cursor-pointer"
                      >
                        {service.shortName}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/services" className="cursor-pointer font-medium text-primary">
                      View All Services
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                  Locations <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-card border-border z-50 min-w-[220px]">
                  {locations.map((loc, index) => (
                    <div key={loc.slug}>
                      {index > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel className="flex items-center gap-2 text-primary">
                        <MapPin size={14} />
                        {loc.state}
                      </DropdownMenuLabel>
                      {loc.services.map((service) => (
                        <DropdownMenuItem key={service.slug} asChild>
                          <Link
                            to={`/${loc.slug}/${service.slug}`}
                            className="cursor-pointer pl-6"
                          >
                            {service.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem asChild>
                        <Link
                          to={`/${loc.slug}`}
                          className="cursor-pointer pl-6 font-medium text-primary"
                        >
                          All {loc.state} →
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/locations" className="cursor-pointer font-medium">
                      View All Locations
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/portfolio"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Portfolio
              </Link>

              <Link
                to="/pricing"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Pricing
              </Link>

              <Link
                to="/about"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                About
              </Link>

              <Link to="/book">
                <Button variant="hero" size="default">
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-foreground p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 animate-fade-in">
              <div className="flex flex-col gap-4">
                <Link
                  to="/services"
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  Services
                </Link>
                <div className="space-y-2">
                  <p className="text-xs text-primary font-medium uppercase tracking-wider">
                    Locations
                  </p>
                  <Link
                    to="/washington"
                    className="block text-sm font-medium text-muted-foreground hover:text-primary transition-colors pl-2"
                  >
                    Washington
                  </Link>
                  <Link
                    to="/arkansas"
                    className="block text-sm font-medium text-muted-foreground hover:text-primary transition-colors pl-2"
                  >
                    Arkansas
                  </Link>
                  <Link
                    to="/locations"
                    className="block text-sm font-medium text-primary hover:text-primary/80 transition-colors pl-2"
                  >
                    All Locations →
                  </Link>
                </div>
                <Link
                  to="/portfolio"
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  Portfolio
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  About
                </Link>
                <Link to="/book">
                  <Button variant="hero" size="default" className="w-full mt-2">
                    Book Now
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Header;
